import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Mail, 
  Save, 
  RefreshCw, 
  TestTube, 
  CheckCircle, 
  AlertCircle,
  Server,
  Clock,
  Settings,
  List,
  FileText,
  Activity
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const EmailConfiguration = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // State for different configuration sections
  const [emailConfig, setEmailConfig] = useState({});
  const [smtpConfig, setSmtpConfig] = useState({});
  const [queueSettings, setQueueSettings] = useState({});
  const [emailDefaults, setEmailDefaults] = useState({});

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const [configRes, smtpRes, queueRes, defaultsRes] = await Promise.all([
        MockDataService.getEmailConfiguration(),
        MockDataService.getSMTPConfiguration(),
        MockDataService.getEmailQueueSettings(),
        MockDataService.getEmailDefaults()
      ]);

      if (configRes && configRes.success) setEmailConfig(configRes.data || {});
      if (smtpRes && smtpRes.success) setSmtpConfig(smtpRes.data || {});
      if (queueRes && queueRes.success) setQueueSettings(queueRes.data || {});
      if (defaultsRes && defaultsRes.success) setEmailDefaults(defaultsRes.data || {});
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load email configuration' });
      setEmailConfig({});
      setSmtpConfig({});
      setQueueSettings({});
      setEmailDefaults({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving configuration
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update localStorage for persistence
      localStorage.setItem('emailConfiguration', JSON.stringify(emailConfig));
      localStorage.setItem('smtpConfiguration', JSON.stringify(smtpConfig));
      localStorage.setItem('emailQueueSettings', JSON.stringify(queueSettings));
      localStorage.setItem('emailDefaults', JSON.stringify(emailDefaults));
      
      setMessage({ type: 'success', text: 'Email configuration saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save email configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      // Simulate connection testing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock test result
      const success = Math.random() > 0.2; // 80% success rate for demo
      
      if (success) {
        setMessage({ type: 'success', text: 'SMTP connection test successful!' });
        setEmailConfig({
          ...emailConfig,
          smtp: {
            ...emailConfig.smtp,
            testConnection: true,
            lastTested: new Date().toISOString()
          }
        });
      } else {
        setMessage({ type: 'error', text: 'SMTP connection test failed. Please check your settings.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    setTesting(true);
    try {
      // Simulate test email sending
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setMessage({ type: 'success', text: 'Test email sent successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading email configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Email Configuration</h1>
          <p className="text-gray-600">Configure system-wide email settings and SMTP server</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadConfiguration}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </div>
      </div>

      {message.text && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('smtp')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'smtp'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Server className="h-4 w-4 inline mr-2" />
              SMTP Server
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              Email Settings
            </button>
            <button
              onClick={() => setActiveTab('queue')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'queue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="h-4 w-4 inline mr-2" />
              Queue Management
            </button>
            <button
              onClick={() => setActiveTab('defaults')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'defaults'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Defaults
            </button>
          </nav>
        </div>

        {activeTab === 'smtp' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                SMTP Server Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtpHost">SMTP Host</Label>
                  <Input
                    id="smtpHost"
                    value={emailConfig.smtp?.host || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, host: e.target.value }
                    })}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtpPort">Port</Label>
                  <Input
                    id="smtpPort"
                    value={emailConfig.smtp?.port || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, port: e.target.value }
                    })}
                    placeholder="587"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtpUsername">Username</Label>
                  <Input
                    id="smtpUsername"
                    value={emailConfig.smtp?.username || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, username: e.target.value }
                    })}
                    placeholder="support@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtpPassword">Password</Label>
                  <Input
                    id="smtpPassword"
                    type="password"
                    value={emailConfig.smtp?.password || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, password: e.target.value }
                    })}
                    placeholder="Enter password"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="encryption">Encryption</Label>
                  <Select
                    value={emailConfig.smtp?.encryption || 'TLS'}
                    onValueChange={(value) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, encryption: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="TLS">TLS</SelectItem>
                      <SelectItem value="SSL">SSL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="authentication">Authentication</Label>
                  <Select
                    value={emailConfig.smtp?.authentication || 'password'}
                    onValueChange={(value) => setEmailConfig({
                      ...emailConfig,
                      smtp: { ...emailConfig.smtp, authentication: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="password">Username/Password</SelectItem>
                      <SelectItem value="oauth2">OAuth 2.0</SelectItem>
                      <SelectItem value="api_key">API Key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge variant={emailConfig.smtp?.testConnection ? 'default' : 'secondary'}>
                    {emailConfig.smtp?.testConnection ? 'Connected' : 'Not Tested'}
                  </Badge>
                  {emailConfig.smtp?.lastTested && (
                    <span className="text-sm text-gray-600">
                      Last tested: {new Date(emailConfig.smtp.lastTested).toLocaleString()}
                    </span>
                  )}
                </div>
                <Button onClick={handleTestConnection} disabled={testing}>
                  {testing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Email Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fromName">From Name</Label>
                  <Input
                    id="fromName"
                    value={emailConfig.settings?.fromName || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, fromName: e.target.value }
                    })}
                    placeholder="HelpDesk Support"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fromEmail">From Email</Label>
                  <Input
                    id="fromEmail"
                    value={emailConfig.settings?.fromEmail || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, fromEmail: e.target.value }
                    })}
                    placeholder="support@company.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="replyTo">Reply-To Email</Label>
                  <Input
                    id="replyTo"
                    value={emailConfig.settings?.replyTo || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, replyTo: e.target.value }
                    })}
                    placeholder="noreply@company.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bounceEmail">Bounce Email</Label>
                  <Input
                    id="bounceEmail"
                    value={emailConfig.settings?.bounceEmail || ''}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, bounceEmail: e.target.value }
                    })}
                    placeholder="bounces@company.com"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="maxRecipients">Max Recipients</Label>
                  <Input
                    id="maxRecipients"
                    type="number"
                    value={emailConfig.settings?.maxRecipients || 100}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, maxRecipients: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="1000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="maxAttachmentSize">Max Attachment Size</Label>
                  <Select
                    value={emailConfig.settings?.maxAttachmentSize || '10MB'}
                    onValueChange={(value) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, maxAttachmentSize: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5MB">5MB</SelectItem>
                      <SelectItem value="10MB">10MB</SelectItem>
                      <SelectItem value="25MB">25MB</SelectItem>
                      <SelectItem value="50MB">50MB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableHTML">Enable HTML Emails</Label>
                    <p className="text-sm text-gray-600">Allow HTML content in emails</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableHTML"
                    checked={emailConfig.settings?.enableHTML || false}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, enableHTML: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableTracking">Enable Email Tracking</Label>
                    <p className="text-sm text-gray-600">Track email opens and clicks</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableTracking"
                    checked={emailConfig.settings?.enableTracking || false}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, enableTracking: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableBounceHandling">Enable Bounce Handling</Label>
                    <p className="text-sm text-gray-600">Automatically handle bounced emails</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableBounceHandling"
                    checked={emailConfig.settings?.enableBounceHandling || false}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      settings: { ...emailConfig.settings, enableBounceHandling: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'queue' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <List className="h-5 w-5" />
                      Email Queue Management
                    </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fetchInterval">Fetch Interval (minutes)</Label>
                  <Input
                    id="fetchInterval"
                    type="number"
                    value={emailConfig.frequency?.fetchInterval || 5}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      frequency: { ...emailConfig.frequency, fetchInterval: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="60"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sendInterval">Send Interval (minutes)</Label>
                  <Input
                    id="sendInterval"
                    type="number"
                    value={emailConfig.frequency?.sendInterval || 1}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      frequency: { ...emailConfig.frequency, sendInterval: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="60"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retryAttempts">Retry Attempts</Label>
                  <Input
                    id="retryAttempts"
                    type="number"
                    value={emailConfig.frequency?.retryAttempts || 3}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      frequency: { ...emailConfig.frequency, retryAttempts: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableAutoFetch">Enable Auto Fetch</Label>
                    <p className="text-sm text-gray-600">Automatically fetch emails from configured accounts</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableAutoFetch"
                    checked={emailConfig.frequency?.enableAutoFetch || false}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      frequency: { ...emailConfig.frequency, enableAutoFetch: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableAutoSend">Enable Auto Send</Label>
                    <p className="text-sm text-gray-600">Automatically send queued emails</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableAutoSend"
                    checked={emailConfig.frequency?.enableAutoSend || false}
                    onChange={(e) => setEmailConfig({
                      ...emailConfig,
                      frequency: { ...emailConfig.frequency, enableAutoSend: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Queue Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Queue Status</Label>
                    <Badge variant={queueSettings.queueStatus === 'active' ? 'default' : 'secondary'}>
                      {queueSettings.queueStatus || 'Inactive'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <Label>Queue Size</Label>
                    <span className="text-lg font-semibold">{queueSettings.queueSize || 0}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Processing Rate</Label>
                    <span className="text-lg font-semibold">{queueSettings.processingRate || '0 emails/minute'}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Last Processed</Label>
                    <span className="text-sm text-gray-600">
                      {queueSettings.lastProcessed ? new Date(queueSettings.lastProcessed).toLocaleString() : 'Never'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'defaults' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Email Templates & Defaults
                    </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Default Templates</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="welcomeSubject">Welcome Email Subject</Label>
                    <Input
                      id="welcomeSubject"
                      value={emailDefaults.templates?.welcome?.subject || ''}
                      onChange={(e) => setEmailDefaults({
                        ...emailDefaults,
                        templates: {
                          ...emailDefaults.templates,
                          welcome: { ...emailDefaults.templates?.welcome, subject: e.target.value }
                        }
                      })}
                      placeholder="Welcome to Support"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="welcomeBody">Welcome Email Body</Label>
                    <textarea
                      id="welcomeBody"
                      className="w-full p-3 border rounded-md"
                      rows={3}
                      value={emailDefaults.templates?.welcome?.body || ''}
                      onChange={(e) => setEmailDefaults({
                        ...emailDefaults,
                        templates: {
                          ...emailDefaults.templates,
                          welcome: { ...emailDefaults.templates?.welcome, body: e.target.value }
                        }
                      })}
                      placeholder="Thank you for contacting our support team..."
                    />
                  </div>
                </div>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold">Available Variables</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {emailDefaults.variables?.map((variable, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <code className="text-sm font-mono">{variable.name}</code>
                        <span className="text-sm text-gray-600">{variable.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold">Default Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="defaultPriority">Default Priority</Label>
                      <Select
                        value={emailDefaults.settings?.defaultPriority || 'Normal'}
                        onValueChange={(value) => setEmailDefaults({
                          ...emailDefaults,
                          settings: { ...emailDefaults.settings, defaultPriority: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Normal">Normal</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="defaultDepartment">Default Department</Label>
                      <Select
                        value={emailDefaults.settings?.defaultDepartment || 'Support'}
                        onValueChange={(value) => setEmailDefaults({
                          ...emailDefaults,
                          settings: { ...emailDefaults.settings, defaultDepartment: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Support">Support</SelectItem>
                          <SelectItem value="Technical">Technical</SelectItem>
                          <SelectItem value="Sales">Sales</SelectItem>
                          <SelectItem value="Billing">Billing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Test Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="testEmail">Test Email Address</Label>
                <Input
                  id="testEmail"
                  type="email"
                  placeholder="test@example.com"
                />
              </div>
              <Button onClick={handleSendTestEmail} disabled={testing}>
                {testing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send Test Email
              </Button>
            </CardContent>
          </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfiguration;
